import { nanoid } from 'nanoid'
import { set as _set } from 'radash'

import {
	SetResponseDto,
	SetsResponseDto,
	UpdateSetRequestDto
} from '@wwt/entities/api/search-response'

import { assert } from '@/shared/lib/assert'

import { SetTypes } from '../config/setKeys'

const generateSet = (fields: Partial<SetResponseDto>) => {
	return {
		id: nanoid(),
		title: 'New set',
		isSelected: true,
		mode: 'ALL',
		totalOffers: 0,
		...fields
	} satisfies SetResponseDto
}

const addSelectedSetToList = (
	list: SetsResponseDto | undefined,
	newSelectedSet: SetResponseDto
): SetsResponseDto => {
	// DEV: Add description
	assert(list !== undefined)
	// DEV: Add description
	assert(newSelectedSet.isSelected === true)

	return {
		sets: [
			...list.sets.map(set => _set(set, 'isSelected', false)),
			newSelectedSet
		]
	}
}

const replaceTemporalySetWithNew = (
	list: SetsResponseDto | undefined,
	tempId: SetResponseDto['id'],
	newSelectedSet: SetResponseDto
): SetsResponseDto => {
	// DEV: Add description
	assert(list !== undefined)
	// DEV: Add description
	assert(newSelectedSet.isSelected === true)

	return {
		sets: list.sets.map(set => (set.id === tempId ? newSelectedSet : set))
	}
}

const removeSetFromList = (
	list: SetsResponseDto | undefined,
	id: SetResponseDto['id']
): SetsResponseDto => {
	// DEV: Add description
	assert(list !== undefined)
	// We cannot delete last set in list
	assert(list.sets.length > 1)

	// TODO: Remove structuredClone
	// eslint-disable-next-line
	const setsCopy = structuredClone(list.sets)

	const isIdInList = setsCopy.find(set => set.id === id)
	assert(isIdInList !== undefined)

	const listWithoutSet = setsCopy.filter(set => set.id !== id)
	assert(listWithoutSet.length > 0)

	if (isIdInList.isSelected) {
		listWithoutSet[0].isSelected = true
	}

	return {
		sets: listWithoutSet
	}
}

const updateSetInList = (
	list: SetsResponseDto | undefined,
	id: SetResponseDto['id'],
	update: UpdateSetRequestDto
): SetsResponseDto => {
	assert(list !== undefined)

	// We should not change selected to false with one set in list
	if (update.isSelected === false) {
		assert(list.sets.length >= 2)
	}

	// TODO: Remove structuredClone
	// eslint-disable-next-line
	const setsCopy = structuredClone(list.sets)

	const listWithUpdatedOffer = setsCopy.map(set => {
		if (set.id === id) {
			return { ...set, ...update }
		}

		if (update.isSelected === true && set.isSelected === true) {
			set.isSelected = false
		}

		return set
	})

	if (update.isSelected === true) {
		return { sets: listWithUpdatedOffer }
	}

	for (let i = 0; i < listWithUpdatedOffer.length; i++) {
		if (listWithUpdatedOffer[i].id === id) continue

		listWithUpdatedOffer[i].isSelected = true

		return { sets: listWithUpdatedOffer }
	}

	throw new Error('Something went wrong on update set in list')
}

const addNumberToTotalOffers = (
	saved: SetTypes['all'] | undefined,
	setId: string | undefined,
	number: number
) => {
	assert(
		saved !== undefined,
		'Saved sets is undefined on add offer to total offers'
	)
	assert(
		saved !== undefined,
		'Set id is undefined on add offer to total offers'
	)

	return {
		sets: saved.sets.map(set =>
			set.id === setId
				? _set(set, 'totalOffers', set.totalOffers + number)
				: set
		)
	}
}

export const SetMutationUtils = {
	generateSet,
	addSelectedSetToList,
	replaceTemporalySetWithNew,
	removeSetFromList,
	updateSetInList,
	addNumberToTotalOffers
}
